import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingIndicator, ScreenReaderText } from '@mlc/web-ui-toolkit';
import _ from 'lodash';
import React from 'react';
import Highlighter from 'react-highlight-words';
import { SearchDataItem } from '../../../../logic/domains/search.domain';
import {
	addFormEvent,
	EVENT_FORM_COMPLETED,
} from '../../../../logic/services/digital-data.service';
import { Autosuggest } from '../Autosuggest/Autosuggest';
import { SearchElements } from '../SearchElements';
import AppConfig from '../../../../AppConfig';
import * as Styled from './styled';
import { getSuggestions } from './suggestions';

interface Props {
	onChangedFn: (searchQuery: string) => void;
	onSelectedFn: (
		profileNumber: string,
		profileToken: string,
		type: string
	) => void;
	onSearchFn: () => void;
	data: Array<SearchDataItem> | null;
	recentClients: Array<SearchDataItem> | null;
	className?: string;
	analyticsId: string;
}

interface State {
	value: string;
	sections: Array<Section>;
	liveContent: string;
}

const initialState: State = {
	value: '',
	sections: [
		{
			title: 'Client',
			list: [],
		},
		{
			title: 'Plan',
			list: [],
		},
		{
			title: 'Group',
			list: [],
		},
	],
	liveContent: '',
};

class QuickSearch extends React.Component<Props, State> {
	private readonly elements = SearchElements;
	private searchList: Array<SearchDataItem> | null = null;
	private recentClientsSection: Section = { title: 'Recent Clients', list: [] };
	private isSelected: boolean = false;
	private readonly regEx = new RegExp('^[0-9]+$');

	constructor(props: Props) {
		super(props);
		this.state = initialState;
		this.initRecentClients(props);
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.recentClients !== prevProps.recentClients) {
			this.initRecentClients(prevProps);
		}
		if (this.props.data !== prevProps.data && _.isEmpty(prevProps.data)) {
			this.initSuggestions();
		}
	}

	initRecentClients(prevProps: Props) {
		if (this.props.recentClients) {
			this.recentClientsSection.list = this.props.recentClients;
			if (!prevProps.recentClients) {
				this.updateRecentClientsOrSuggestions(this.state.value);
			}
		}
	}

	initSuggestions() {
		this.updateRecentClientsOrSuggestions(this.state.value);
	}

	handleSearchInputChange = (event: any, { newValue, method }: any) => {
		if (method === 'type') {
			this.setState({ value: newValue });
			this.props.onChangedFn(newValue);
		} else if (method === 'enter') {
			this.isSelected = true;
		}
	};

	handleSuggestionSelected = (
		event: any,
		{ suggestion, suggestionValue }: any
	) => {
		this.props.onSelectedFn(suggestionValue, suggestion.token, suggestion.type);
	};

	handleSuggestionsFetchRequested = ({ value }: any) => {
		this.updateRecentClientsOrSuggestions(value);
	};

	updateRecentClientsOrSuggestions = (value: any) => {
		if (value.length <= 2) {
			this.setState({
				sections: [this.recentClientsSection],
				liveContent: '',
			});
		} else if (this.props.data) {
			const suggestions = getSuggestions(value, this.props.data);
			const totalSuggestions = _.sumBy(
				suggestions,
				section => section.list.length
			);
			// Clear liveContent for aria-atomic
			this.setState(
				{
					sections: suggestions,
					liveContent: '',
				},
				() => {
					this.setState({
						liveContent: this.generateLiveContent(totalSuggestions),
					});
				}
			);
		}
	};

	handleSuggestionsClearRequested = () => {
		this.setState({ sections: [] });
	};

	getSuggestionValue = (suggestion: SearchDataItem) => suggestion.id;

	shouldRenderSuggestions = () => {
		return this.state.value.length >= 0;
	};

	renderSectionTitle = (section: Section) => {
		return (
			<span>
				{section.title.charAt(0).toUpperCase() + section.title.slice(1)}
			</span>
		);
	};

	renderSuggestion = (details: SearchDataItem, queryDetails: any) => {
		return (
			<span data-analytics-id="search-autosuggest-result">
				<Highlighter
					highlightStyle={{
						fontWeight: 700,
						backgroundColor: 'transparent',
						color: 'inherit',
					}}
					searchWords={[queryDetails.query]}
					autoEscape={true}
					textToHighlight={this.getSuggestionName(details)}
				/>
			</span>
		);
	};

	getSuggestionName = (suggestion: SearchDataItem): string => {
		if (this.regEx.test(this.state.value) && this.state.value.length > 2) {
			if (suggestion.matchedAccountID) {
				return suggestion.matchedAccountID + ' - ' + suggestion.name;
			} else {
				return suggestion.id + ' - ' + suggestion.name;
			}
		} else {
			return suggestion.name;
		}
	};

	generateLiveContent = (totalSuggestions: Number) => {
		const pluralForm = totalSuggestions !== 1;
		const copula = pluralForm ? 'are' : 'is';
		const suggestionWord = pluralForm ? 'suggestions' : 'suggestion';
		return totalSuggestions > 0
			? `There ${copula} currently ${totalSuggestions} ${suggestionWord} displayed.`
			: this.state.value.length > 2
			? 'No results found. Refine your search and try again.'
			: '';
	};

	public render() {
		const { value, sections } = this.state;
		const inputProps = {
			onChange: this.handleSearchInputChange,
			placeholder: this.elements.autoSuggestPlaceholder,
			value,
		};

		const { onSearchFn, analyticsId } = this.props;

		return (
			<Styled.SearchForm
				id={analyticsId}
				className={this.props.className}
				onSubmit={(event: any) => {
					event.preventDefault();
					addFormEvent(EVENT_FORM_COMPLETED, analyticsId, !!value);
					onSearchFn();
				}}
				noValidate
			>
				<ScreenReaderText id={this.elements.qsSearchHelp}>
					Suggestions appear below as you type
				</ScreenReaderText>
				<ScreenReaderText aria-live="polite" aria-atomic="true">
					{this.state.liveContent}
				</ScreenReaderText>
				<label
					htmlFor={this.elements.autoSuggestId}
					tabIndex={-1}
					className="sr-only"
				>
					Client search
				</label>
				<Autosuggest
					aria-describedby={this.elements.qsSearchHelp}
					id={this.elements.autoSuggestId}
					multiSection={true}
					suggestions={sections}
					onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
					onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
					onSuggestionSelected={this.handleSuggestionSelected}
					getSuggestionValue={this.getSuggestionValue}
					inputProps={inputProps}
					shouldRenderSuggestions={this.shouldRenderSuggestions}
					renderSuggestion={this.renderSuggestion}
					renderSectionTitle={this.renderSectionTitle}
					getSectionSuggestions={(section: Section) => section.list}
				/>
				<Styled.SearchButton
					id={this.elements.searchBtnId}
					data-analytics-id="search-button"
					type="submit"
					buttonImage={
						AppConfig.aemImageEnv + 'online/resource/images/Search_white.png'
					}
				>
					Search
					<Styled.SearchIcon>
						<FontAwesomeIcon
							icon={{ prefix: 'far', iconName: 'search' }}
							style={{ width: '14px' }}
						/>
					</Styled.SearchIcon>
				</Styled.SearchButton>
				{this.props.data === null ? (
					<Styled.LoadingWrapper>
						<LoadingIndicator />
					</Styled.LoadingWrapper>
				) : null}
			</Styled.SearchForm>
		);
	}
}

export default QuickSearch;

export interface ClientData {
	[index: string]: Array<ClientDetails>;
	clients: Array<ClientDetails>;
	plans: Array<ClientDetails>;
}

export interface ClientDetails {
	type?: string;
	label: string;
	link: string;
	numbers: Array<string>;
}

interface Section {
	title: string;
	list: Array<SearchDataItem>;
}
